/**
 * Modern Supplier Case Creation Form V2
 * Enhanced with multiple supplier selection and type-specific fields
 * Supports: quote_request, order_issue, follow_up with file uploads
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Grid,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  RequestQuote as QuoteIcon,
  ReportProblem as IssueIcon,
  Email as FollowUpIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as UploadIcon,
  AttachFile as AttachFileIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';
import { caseManagementService, CreateSupplierCaseRequest } from '../../../services/caseManagementService';
import { unifiedOrderService } from '../../../services/unifiedOrderService';
import { 
  MaterialData, 
  HeatTreatmentData, 
  CoatingData, 
  ToolData, 
  CarbideData,
  OrderPosition 
} from '../../../types/unifiedOrderTypes';
import { MaterialFields, HeatTreatmentFields, CoatingFields, ToolFields, CarbideFields } from '../../forms/TypeSpecificFields';
import SupplierSelector from './SupplierSelector';
import { Supplier } from '../../../services/supplierService';

interface ModernSupplierCaseCreationFormProps {
  open: boolean;
  onClose: () => void;
  onCaseCreated: (caseData: any) => void;
  initialCaseSubType?: 'quote_request' | 'order_issue' | 'follow_up';
  emailContext?: {
    email_id: string;
    from_address: string;
    subject: string;
    body_text?: string;
  };
}

interface SupplierInfo {
  id: string;
  email: string;
  name?: string;
  company?: string;
}


interface PositionData {
  position_number: number;
  notes?: string;         // Renamed from 'description' to match backend
  quantity: number;
  part_name?: string;
  part_number?: string;
  // Changed from File object to uploaded file metadata
  uploadedFile?: {
    file_id: string;
    file_path: string;
    filename: string;
    file_size: number;
  };
  type_specific_data: MaterialData | HeatTreatmentData | CoatingData | ToolData | CarbideData;
}

interface SupplierCaseFormData {
  case_sub_type: 'quote_request' | 'order_issue' | 'follow_up';
  suppliers: SupplierInfo[];
  title: string;
  description?: string;
  priority: number;
  
  // Quote request fields - Order Header
  order_type?: 'material' | 'coating' | 'carbide' | 'tool' | 'heat_treatment';
  order_date?: string;        // Added order date
  due_date?: string;          // Added due date  
  order_notes?: string;       // Added order-level notes
  positions?: PositionData[];
  
  // Order issue fields
  related_order_id?: string;
  issue_type?: string;
  original_quantity?: number;
  proposed_quantity?: number;
  
  // Email settings
  auto_reply_enabled: boolean;
}

  const defaultPosition: PositionData = {
    position_number: 1,
    notes: '',
    quantity: 0,
    type_specific_data: { material_spec: '' } as CarbideData // Default to carbide with minimal required field
  };

export default function ModernSupplierCaseCreationForm({
  open,
  onClose,
  onCaseCreated,
  initialCaseSubType = 'quote_request',
  emailContext
}: ModernSupplierCaseCreationFormProps) {
  // Add a unique identifier to confirm this is the correct form
  useEffect(() => {
    if (open) {
      console.log('🚀 =============== COMPONENT LIFECYCLE START ===============');
      console.log('🏷️ Component: ModernSupplierCaseCreationForm');
      console.log('⏰ Mount Timestamp:', new Date().toISOString());
      console.log('📋 Props Analysis:', {
        component_version: 'ENHANCED_WITH_COMPREHENSIVE_DEBUGGING',
        open,
        initial_case_sub_type: initialCaseSubType,
        has_email_context: !!emailContext,
        email_context_detail: emailContext ? {
          email_id: emailContext.email_id,
          from_address: emailContext.from_address,
          subject: emailContext.subject,
          has_body_text: !!emailContext.body_text
        } : null,
        expected_endpoints: [
          'POST /api/v3/cases/supplier-cases (case creation)',
          'POST /api/v3/orders/{orderId}/positions/{positionId}/files (file upload)'
        ],
        expected_db_tables: [
          'cases',
          'case_metadata', 
          'temporary_orders',
          'order_positions',
          'order_position_files',
          'file_associations'
        ]
      });
      console.log('🔧 Component Features:', {
        hasFileUploadFixes: true,
        hasMultiSupplierSupport: true,
        hasTypeSpecificFields: true,
        hasWorkflowDebugging: true,
        hasStepValidation: true
      });
    }
  }, [open]);

  // Separate storage for File objects (won't be serialized with form state)
  const [positionFiles, setPositionFiles] = useState<Map<number, File>>(new Map());
  const [formData, setFormData] = useState<SupplierCaseFormData>({
    case_sub_type: initialCaseSubType,
    suppliers: [{ id: '1', email: '', name: '', company: '' }],
    title: '',
    description: '',
    priority: 3,
    order_type: 'material',
    order_date: new Date().toISOString().split('T')[0], // Default to today
    due_date: '',
    order_notes: '',
    positions: [{ ...defaultPosition }],
    auto_reply_enabled: true
  });

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const { showNotification } = useNotification();

  const steps = [
    'Case Type & Suppliers',
    'Case Details',
    'Positions & Specifications',
    'Review & Create'
  ];

  // Initialize from email context
  useEffect(() => {
    if (emailContext) {
      setFormData(prev => ({
        ...prev,
        suppliers: [{ id: '1', email: emailContext.from_address, name: '', company: '' }],
        title: emailContext.subject || 'Supplier Case',
        description: emailContext.body_text || ''
      }));
    }
  }, [emailContext]);

  // Debug: Monitor uploaded file state changes
  useEffect(() => {
    const positionsWithFiles = formData.positions?.filter(p => !!p.uploadedFile) || [];
    if (positionsWithFiles.length > 0) {
      console.log('📊 UPLOADED FILE STATE MONITOR:', {
        timestamp: new Date().toISOString(),
        totalPositions: formData.positions?.length || 0,
        positionsWithFiles: positionsWithFiles.length,
        fileDetails: positionsWithFiles.map((p, i) => ({
          position_number: p.position_number,
          uploadedFileName: p.uploadedFile?.filename,
          uploadedFileSize: p.uploadedFile?.file_size,
          uploadedFileId: p.uploadedFile?.file_id,
          hasUploadedFile: !!p.uploadedFile
        }))
      });
    }
  }, [formData.positions]);

  const validateStep = (stepIndex: number): boolean => {
    const errors: Record<string, string> = {};
    
    switch (stepIndex) {
      case 0: // Case Type & Suppliers
        if (!formData.suppliers || formData.suppliers.length === 0) {
          errors.suppliers = 'At least one supplier required';
        } else {
          const invalidSuppliers = formData.suppliers.filter(s => !s.email);
          if (invalidSuppliers.length > 0) {
            errors.suppliers = 'All suppliers must have valid email addresses';
          }
        }
        if (formData.suppliers && formData.suppliers.length > 12) {
          errors.suppliers = 'Maximum 12 suppliers allowed';
        }
        break;
      case 1: // Case Details
        if (!formData.title) errors.title = 'Title required';
        if (formData.case_sub_type === 'order_issue' && !formData.related_order_id) {
          errors.related_order_id = 'Order ID required for order issues';
        }
        break;
      case 2: // Positions
        if (formData.case_sub_type === 'quote_request' && (!formData.positions || formData.positions.length === 0)) {
          errors.positions = 'At least one position required for quote requests';
        }
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form update helpers
  const updateFormData = (field: keyof SupplierCaseFormData, value: any) => {
    if (field === 'positions') {
      console.log('📝 UPDATING FORM DATA - POSITIONS:', {
        field,
        newValue: value?.map((p: any, i: number) => ({
          index: i,
          hasUploadedFile: !!p.uploadedFile,
          uploadedFileName: p.uploadedFile?.filename,
          uploadedFileSize: p.uploadedFile?.file_size,
          position_number: p.position_number,
          hasValidUploadedFile: !!p.uploadedFile?.file_id
        })),
        timestamp: new Date().toISOString()
      });
      
      // Critical debugging: Check if we're losing uploaded files during update
      const hasFilesInNewValue = value?.some((p: any) => !!p.uploadedFile);
      const hasFilesInCurrentState = formData.positions?.some(p => !!p.uploadedFile);
      
      if (hasFilesInCurrentState && !hasFilesInNewValue) {
        console.error('🚨 UPLOADED FILE LOSS DETECTED during updateFormData!');
        console.error('   Previous state had uploaded files:', formData.positions?.map(p => ({ hasUploadedFile: !!p.uploadedFile, uploadedFileName: p.uploadedFile?.filename })));
        console.error('   New value has uploaded files:', value?.map((p: any) => ({ hasUploadedFile: !!p.uploadedFile, uploadedFileName: p.uploadedFile?.filename })));
        console.error('   Stack trace:', new Error().stack);
      }
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Supplier management
  const addSupplier = () => {
    console.log('[ModernSupplierCaseCreationForm] Adding supplier. Current count:', formData.suppliers.length);
    if (formData.suppliers.length < 12) {
      const newSupplier: SupplierInfo = {
        id: String(formData.suppliers.length + 1),
        email: '',
        name: '',
        company: ''
      };
      console.log('[ModernSupplierCaseCreationForm] New supplier added:', newSupplier);
      updateFormData('suppliers', [...formData.suppliers, newSupplier]);
    } else {
      console.warn('[ModernSupplierCaseCreationForm] Cannot add supplier: maximum of 12 reached');
    }
  };

  const updateSupplier = (index: number, field: keyof SupplierInfo, value: string) => {
    const updatedSuppliers = formData.suppliers.map((supplier, i) =>
      i === index ? { ...supplier, [field]: value } : supplier
    );
    updateFormData('suppliers', updatedSuppliers);
  };

  const removeSupplier = (index: number) => {
    console.log('[ModernSupplierCaseCreationForm] Removing supplier at index:', index);
    if (formData.suppliers.length > 1) {
      const updatedSuppliers = formData.suppliers.filter((_, i) => i !== index);
      console.log('[ModernSupplierCaseCreationForm] Suppliers after removal:', updatedSuppliers);
      updateFormData('suppliers', updatedSuppliers);
    } else {
      console.warn('[ModernSupplierCaseCreationForm] Cannot remove supplier: minimum of 1 required');
    }
  };

  // New supplier selection handler for SupplierSelector
  const handleSupplierSelected = (index: number, supplier: Supplier) => {
    console.log('[ModernSupplierCaseCreationForm] Supplier selected for index', index, ':', supplier);
    const updatedSuppliers = formData.suppliers.map((existingSupplier, i) =>
      i === index ? {
        ...existingSupplier,
        email: supplier.email,
        name: supplier.name,
        company: supplier.company_name
      } : existingSupplier
    );
    console.log('[ModernSupplierCaseCreationForm] Updated suppliers:', updatedSuppliers);
    updateFormData('suppliers', updatedSuppliers);
  };

  // Convert SupplierInfo to Supplier for SupplierSelector
  const getSelectedSupplierForIndex = (index: number): Supplier | null => {
    const supplierInfo = formData.suppliers[index];
    if (!supplierInfo || !supplierInfo.email) return null;
    
    return {
      id: supplierInfo.id,
      name: supplierInfo.name || '',
      company_name: supplierInfo.company || '',
      email: supplierInfo.email,
      phone: '',
      address: '',
      city: '',
      postal_code: ''
    };
  };

  // Position management
  const addPosition = () => {
    const newPosition: PositionData = {
      ...defaultPosition,
      position_number: (formData.positions?.length || 0) + 1, // Use number instead of string
      type_specific_data: getDefaultTypeSpecificData(formData.order_type || 'material')
    };
    updateFormData('positions', [...(formData.positions || []), newPosition]);
  };

  const updatePosition = (index: number, field: keyof PositionData, value: any) => {
    console.log(`🔧 UPDATE POSITION [${index}] - ${field}:`, {
      field,
      value: field === 'uploadedFile' ? `UploadedFile: ${value?.filename} (${value?.file_size} bytes)` : value,
      beforeUpdate: {
        hasUploadedFile: !!formData.positions?.[index]?.uploadedFile,
        uploadedFileName: formData.positions?.[index]?.uploadedFile?.filename,
        [field]: formData.positions?.[index]?.[field]
      }
    });

    const updatedPositions = formData.positions!.map((pos, i) => {
      if (i === index) {
        const updatedPos = { ...pos, [field]: value };
        
        // CRITICAL FIX: Always preserve uploadedFile when NOT explicitly updating it
        if (field !== 'uploadedFile' && pos.uploadedFile) {
          console.log(`🛡️ PRESERVING UPLOADED FILE during ${field} update for position ${index}`);
          updatedPos.uploadedFile = pos.uploadedFile;
        }
        
        return updatedPos;
      }
      return pos;
    });
    
    console.log(`✅ POSITION UPDATE COMPLETE [${index}]:`, {
      updatedPosition: {
        hasUploadedFile: !!updatedPositions[index]?.uploadedFile,
        uploadedFileName: updatedPositions[index]?.uploadedFile?.filename,
        filePreserved: field === 'type_specific_data' ? (!!formData.positions?.[index]?.uploadedFile === !!updatedPositions[index]?.uploadedFile) : 'N/A',
        [field]: updatedPositions[index]?.[field]
      }
    });
    
    updateFormData('positions', updatedPositions);
  };

  const removePosition = (index: number) => {
    if (formData.positions!.length > 1) {
      const updatedPositions = formData.positions!.filter((_, i) => i !== index);
      updateFormData('positions', updatedPositions);
    }
  };

  // File upload handling - Enhanced with comprehensive debugging
  const handleFileUpload = (index: number, file: File) => {
    console.log('\n� =============== FILE UPLOAD HANDLER START ===============');
    console.log('🎯 Function: handleFileUpload');
    console.log('⏰ Timestamp:', new Date().toISOString());
    console.log('📊 Upload Context:', { 
      position_index: index, 
      file_name: file.name, 
      file_size: file.size,
      file_type: file.type,
      file_last_modified: new Date(file.lastModified).toISOString(),
      current_step: step,
      order_type: formData.order_type,
      total_positions: formData.positions?.length || 0
    });
    
    console.log('🔍 Pre-Upload State Analysis:', {
      position_files_map_size: positionFiles.size,
      existing_files: Array.from(positionFiles.entries()).map(([idx, f]) => ({
        index: idx,
        filename: f.name
      })),
      form_metadata_state: formData.positions?.map((p, i) => ({
        index: i,
        has_uploaded_file_metadata: !!p.uploadedFile,
        metadata_filename: p.uploadedFile?.filename
      })) || []
    });
    
    try {
      console.log('   📝 Step 1: Storing File object in separate Map (prevents serialization issues)');
      // Store the File object in a separate Map (won't be serialized with form state)
      const newFileMap = new Map(positionFiles);
      newFileMap.set(index, file);
      setPositionFiles(newFileMap);
      
      console.log('   📝 Step 2: Creating serializable metadata for form state');
      // Store only display metadata in form state (safe for serialization)
      const fileMetadata = {
        file_id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Temporary ID for display
        file_path: '', // Will be set after actual upload
        filename: file.name,
        file_size: file.size
      };
      
      console.log('   📝 Step 3: Updating position with file metadata');
      updatePosition(index, 'uploadedFile', fileMetadata);
      
      showNotification(`✅ ${file.name} ready for upload`, 'success');
      
      console.log('✅ FILE UPLOAD HANDLER SUCCESS:', {
        position_index: index,
        new_map_size: newFileMap.size,
        file_metadata: fileMetadata,
        timestamp: new Date().toISOString(),
        next_phase: 'File will be uploaded during form submission'
      });
      
    } catch (error: any) {
      console.error('💥 FILE UPLOAD HANDLER FAILED:', {
        position_index: index,
        file_name: file.name,
        error_message: error.message,
        error_stack: error.stack
      });
      showNotification(`Failed to attach ${file.name}: ${error.message}`, 'error');
    }
    console.log('=============== FILE UPLOAD HANDLER END ===============\n');
  };

  // Type-specific data helpers
  const getDefaultTypeSpecificData = (orderType: string): MaterialData | HeatTreatmentData | CoatingData | ToolData | CarbideData => {
    switch (orderType) {
      case 'carbide':
        return { 
          material_spec: '' 
        } as CarbideData;
      case 'material':
        return { 
          form: '', 
          steel_quality: '',
          cross_section: '',
          length_mm: undefined,
          internal_order_reference: ''
        } as MaterialData;
      case 'tool':
        return { 
          tool_type: '', 
          manufacturer: '', 
          manufacturer_article_number: '', 
          additional_info: '' 
        } as ToolData;
      case 'coating':
        return { 
          treatment_type: '', 
          coating_spec: '', 
          steel_quality: '',
          weight_kg: undefined,
          internal_order_reference: '',
          additional_info: ''
        } as CoatingData;
      case 'heat_treatment':
        return { 
          treatment_type: '', 
          steel_quality: '',
          hrc_range: '',
          weight_kg: undefined,
          internal_order_reference: '',
          additional_info: ''
        } as HeatTreatmentData;
      default:
        return { material_spec: '' } as CarbideData;
    }
  };

  // Order type change handler - reset type-specific data when order type changes
  // FIXED: Only update if type_specific_data actually needs to change, preserve uploaded files
  // CRITICAL FIX: Use useRef to track previous order type to prevent unnecessary updates
  const previousOrderType = React.useRef(formData.order_type);
  
  useEffect(() => {
    // Only run if order type actually changed and we have positions
    if (formData.order_type && formData.positions && previousOrderType.current !== formData.order_type) {
      console.log('🔄 ORDER TYPE CHANGE EFFECT TRIGGERED:', {
        previousType: previousOrderType.current,
        newType: formData.order_type,
        positionsCount: formData.positions.length,
        positionsWithUploadedFiles: formData.positions.map((p, i) => ({
          index: i,
          hasUploadedFile: !!p.uploadedFile,
          uploadedFileName: p.uploadedFile?.filename,
          uploadedFileSize: p.uploadedFile?.file_size
        }))
      });
      
      const newDefaultData = getDefaultTypeSpecificData(formData.order_type!);
      
      // Only update positions that don't have the correct type-specific structure
      const needsUpdate = formData.positions.some(pos => {
        const currentKeys = Object.keys(pos.type_specific_data || {});
        const newKeys = Object.keys(newDefaultData);
        return !newKeys.every(key => currentKeys.includes(key));
      });
      
      console.log('🔍 ORDER TYPE EFFECT - Needs update:', needsUpdate);
      
      if (needsUpdate) {
        console.log('⚠️ UPDATING POSITIONS - Preserving uploaded files!');
        
        const updatedPositions = formData.positions.map((pos, index) => {
          console.log(`   Updating position ${index}:`, {
            beforeUpdate: {
              hasUploadedFile: !!pos.uploadedFile,
              uploadedFileName: pos.uploadedFile?.filename,
              typeSpecificKeys: Object.keys(pos.type_specific_data || {})
            }
          });
          
          const updated = {
            ...pos,
            // Preserve existing data and only add missing keys
            type_specific_data: {
              ...newDefaultData,
              ...pos.type_specific_data
            }
          };
          
          console.log(`   After update position ${index}:`, {
            hasUploadedFile: !!updated.uploadedFile,
            uploadedFileName: updated.uploadedFile?.filename,
            uploadedFilePreserved: !!pos.uploadedFile === !!updated.uploadedFile
          });
          
          return updated;
        });
        
        updateFormData('positions', updatedPositions);
        console.log('✅ ORDER TYPE EFFECT COMPLETE');
      } else {
        console.log('✅ No update needed - uploaded files preserved');
      }
      
      // Update the ref to track the current order type
      previousOrderType.current = formData.order_type;
    }
  }, [formData.order_type]); // FIXED: Remove formData.positions from dependencies

  // Navigation with enhanced debugging
  const handleNext = () => {
    console.log('\n🔄 =============== STEP NAVIGATION START ===============');
    console.log('🎯 Function: handleNext');
    console.log('📊 Navigation Context:', {
      current_step: step,
      target_step: step + 1,
      step_name: steps[step],
      next_step_name: steps[step + 1],
      total_steps: steps.length
    });
    
    console.log('🔍 Pre-Navigation State:', {
      form_data_completeness: {
        suppliers_count: formData.suppliers.length,
        has_title: !!formData.title,
        has_positions: formData.positions?.length || 0,
        positions_with_files: formData.positions?.filter(p => !!p.uploadedFile).length || 0
      },
      validation_ready: true
    });
    
    if (validateStep(step)) {
      console.log('✅ Step validation passed');
      // Debug: Check uploaded file state before navigation
      console.log('� File State Before Navigation:', {
        position_files_map_size: positionFiles.size,
        positions_with_file_metadata: formData.positions?.map((p, i) => ({
          index: i,
          has_uploaded_file: !!p.uploadedFile,
          uploaded_file_name: p.uploadedFile?.filename,
          uploaded_file_size: p.uploadedFile?.file_size
        })) || []
      });
      
      const newStep = Math.min(step + 1, steps.length - 1);
      setStep(newStep);
      
      console.log('📝 Navigation complete:', {
        previous_step: step,
        new_step: newStep,
        step_name: steps[newStep]
      });
    } else {
      console.log('❌ Step validation failed - staying on current step');
    }
    console.log('=============== STEP NAVIGATION END ===============\n');
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 0));
  };

  // Form submission - Enhanced with comprehensive debugging and workflow tracing
  const handleSubmit = async () => {
    console.log('🚀 =============== SUPPLIER CASE CREATION WORKFLOW START ===============');
    console.log('🎯 Component: ModernSupplierCaseCreationForm.tsx');
    console.log('⏰ Timestamp:', new Date().toISOString());
    console.log('📍 Step: Form Submission');
    
    // PHASE 1: FORM STATE ANALYSIS
    console.log('\n📊 PHASE 1: FORM STATE ANALYSIS');
    console.log('   Form Data Structure:', {
      case_type: 'supplier_case',
      case_sub_type: formData.case_sub_type,
      suppliers_count: formData.suppliers.length,
      suppliers_detail: formData.suppliers.map((s, i) => ({
        index: i,
        email: s.email,
        name: s.name,
        company: s.company,
        has_email: !!s.email
      })),
      positions_count: formData.positions?.length || 0,
      positions_detail: formData.positions?.map(p => ({
        position_number: p.position_number,
        quantity: p.quantity,
        part_name: p.part_name,
        has_notes: !!p.notes,
        has_type_specific_data: !!p.type_specific_data,
        type_specific_keys: Object.keys(p.type_specific_data || {}),
        has_uploaded_file: !!p.uploadedFile,
        uploaded_file_name: p.uploadedFile?.filename
      })),
      order_type: formData.order_type,
      title: formData.title,
      auto_reply_enabled: formData.auto_reply_enabled
    });
    
    // PHASE 2: FILE UPLOAD ANALYSIS
    console.log('\n📁 PHASE 2: FILE UPLOAD STATE ANALYSIS');
    const fileAnalysis = {
      position_files_map_size: positionFiles.size,
      files_ready_for_upload: Array.from(positionFiles.entries()).map(([index, file]) => ({
        position_index: index,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        is_file_object: file instanceof File,
        last_modified: new Date(file.lastModified).toISOString()
      })),
      form_metadata_vs_actual_files: formData.positions?.map((p, i) => ({
        position_index: i,
        form_has_metadata: !!p.uploadedFile,
        map_has_file: positionFiles.has(i),
        metadata_filename: p.uploadedFile?.filename,
        actual_filename: positionFiles.get(i)?.name,
        files_match: p.uploadedFile?.filename === positionFiles.get(i)?.name
      })) || []
    };
    console.log('   File Analysis:', fileAnalysis);
    
    // Check for files ready to upload
    console.log('🔍 DETAILED FILE ANALYSIS:');
    console.log(`   Files in separate storage: ${positionFiles.size}`);
    Array.from(positionFiles.entries()).forEach(([index, file]) => {
      console.log(`   Position ${index}:`, {
        hasFile: !!file,
        isFileObject: file instanceof File,
        fileName: file.name,
        fileSize: file.size,
        fileLastModified: file.lastModified
      });
    });
    
    if (!validateStep(step)) return;

    setLoading(true);
    try {
      // PHASE 3: API REQUEST PREPARATION
      console.log('\n🔧 PHASE 3: API REQUEST PREPARATION');
      console.log('   Target Endpoint: caseManagementService.createSupplierCase()');
      console.log('   Expected Backend Route: POST /api/v3/cases/supplier-cases');
      
      // Include ALL suppliers in the request (not just the first one)
      console.log('📤 Creating case with multiple suppliers:', formData.suppliers);
      
      const caseCreateRequest: CreateSupplierCaseRequest = {
        case_type: 'supplier_case',
        case_sub_type: formData.case_sub_type,
        // For now, use first supplier for backward compatibility with fields that expect single values
        customer_email: formData.suppliers[0].email,
        customer_name: formData.suppliers[0].name,
        customer_company: formData.suppliers[0].company,
        title: formData.title,
        metadata: {
          order_type: formData.order_type,
          // Include ALL suppliers in metadata
          suppliers: formData.suppliers.map(supplier => ({
            id: supplier.id,
            email: supplier.email,
            name: supplier.name,
            company: supplier.company
          })),
          // Legacy single supplier field for backward compatibility
          supplier_id: formData.suppliers[0].id,
          positions: formData.positions?.map(pos => ({
            position_number: pos.position_number,
            notes: pos.notes || '',
            quantity: pos.quantity,
            part_name: pos.part_name || '',
            part_number: pos.part_number || '',
            type_specific_data: pos.type_specific_data || {}
          })) || [],
          email: formData.auto_reply_enabled ? {
            subject: `Quote Request: ${formData.title}`,
            body_text: formData.description,
            from_address: 'quotes@wzf.de',
            to_addresses: formData.suppliers.map(s => s.email)
          } : undefined
        }
      };

      console.log('   📝 Complete API Request Payload:', JSON.stringify(caseCreateRequest, null, 2));

      // PHASE 4: BACKEND API CALL
      console.log('\n🌐 PHASE 4: BACKEND API CALL');
      console.log('   Calling: caseManagementService.createSupplierCase()');
      console.log('   Expected DB Tables: cases, case_metadata, temporary_orders, order_positions');
      
      // Create the supplier case
      const response = await caseManagementService.createSupplierCase(caseCreateRequest);
      
      console.log('   📨 Raw API Response:', {
        success: response.success,
        message: response.message,
        has_data: !!response.data,
        data_keys: response.data ? Object.keys(response.data) : [],
        response_structure: response.data ? {
          case_id: response.data.id,
          temp_order_id: (response.data as any).temp_order_id,
          status: response.data.status,
          has_positions: !!(response.data as any).positions,
          positions_count: (response.data as any).positions?.length || 0
        } : null
      });
      
      if (!response.success) {
        console.error('❌ PHASE 4 FAILED: API call unsuccessful');
        throw new Error(response.message || 'Failed to create case');
      }

      const createdCase = response.data;
      console.log('✅ PHASE 4 SUCCESS: Case created');
      console.log('   🎯 Created case data:', createdCase);
      
      // PHASE 5: WORKFLOW STATE TRANSITION 
      console.log('\n🔄 PHASE 5: WORKFLOW STATE TRANSITION');
      
      // Ensure we have a valid created case before proceeding
      if (!createdCase?.id) {
        console.error('❌ PHASE 5 ERROR: No valid case ID available for workflow transition');
        throw new Error('Case creation succeeded but no case ID returned');
      }
      
      try {
        console.log('   📝 Transitioning workflow state to "supplier_quote_generated"');
        console.log('   🎯 Expected API Endpoint: PUT /api/v3/workflow/cases/{id}/transition');
        console.log('   📤 Transition Request Payload:', {
          to_state: 'supplier_quote_generated',
          metadata: {
            order_type: formData.order_type,
            positions_count: formData.positions?.length || 0,
            suppliers_count: formData.suppliers.length,
            temp_order_id: (createdCase as any).temp_order_id,
            transition_reason: 'case_creation_complete'
          }
        });
        
        const workflowResponse = await fetch(`/api/v3/workflow/cases/${createdCase.id}/transition`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to_state: 'supplier_quote_generated',
            metadata: {
              order_type: formData.order_type,
              positions_count: formData.positions?.length || 0,
              suppliers_count: formData.suppliers.length,
              temp_order_id: (createdCase as any).temp_order_id,
              transition_reason: 'case_creation_complete'
            }
          })
        });
        
        const workflowResult = await workflowResponse.json();
        
        if (workflowResult.success) {
          console.log('   ✅ Workflow transition successful:', workflowResult.data);
          (createdCase as any).workflow_state = 'supplier_quote_generated';
          (createdCase as any).workflow_metadata = workflowResult.data.metadata;
        } else {
          console.warn('   ⚠️ Workflow transition failed:', workflowResult.message);
        }
      } catch (workflowError) {
        console.error('   ❌ Workflow transition error:', workflowError);
      }

      // PHASE 6: DATABASE RESULT ANALYSIS
      console.log('\n🗃️ PHASE 6: DATABASE RESULT ANALYSIS');
      const caseWithTempOrder = createdCase as any;
      console.log('   📊 Database Insertion Results:', {
        cases_table: {
          case_id: caseWithTempOrder.case_id || caseWithTempOrder.id,
          case_type: caseWithTempOrder.case_type,
          case_sub_type: caseWithTempOrder.case_sub_type,
          status: caseWithTempOrder.status,
          customer_email: caseWithTempOrder.customer_email,
          title: caseWithTempOrder.title,
          created_at: caseWithTempOrder.created_at
        },
        case_metadata_table: {
          has_metadata: !!caseWithTempOrder.metadata,
          metadata_keys: caseWithTempOrder.metadata ? Object.keys(caseWithTempOrder.metadata) : [],
          order_type: caseWithTempOrder.metadata?.order_type,
          suppliers_count: caseWithTempOrder.metadata?.suppliers?.length
        },
        temporary_orders_table: {
          temp_order_id: caseWithTempOrder.temp_order_id,
          has_temp_order: !!caseWithTempOrder.temp_order_id,
          order_type: caseWithTempOrder.order_type
        },
        order_positions_table: {
          positions_created: caseWithTempOrder.positions?.length || 0,
          position_details: caseWithTempOrder.positions?.map((p: any) => ({
            position_id: p.position_id || p.id,
            position_number: p.position_number,
            quantity: p.quantity,
            has_type_specific_data: !!p.type_specific_data
          })) || []
        }
      });
      
      // PHASE 6: FILE UPLOAD WORKFLOW
      console.log('\n📁 PHASE 6: FILE UPLOAD WORKFLOW');
      // Now upload files to the created positions
      let uploadedFilesCount = 0;
      let uploadErrors: string[] = [];
      
      if (caseWithTempOrder?.temp_order_id && positionFiles.size > 0) {
        console.log('   � Starting file uploads for positions...');
        console.log('   📝 Upload Target: unifiedOrderService.uploadPositionFiles()');
        console.log('   🎯 Expected Backend Route: POST /api/v3/orders/{orderId}/positions/{positionId}/files');
        console.log('   🗃️ Expected DB Tables: order_position_files, file_associations');
        
        // Get position mappings from the created case
        const createdPositions = caseWithTempOrder.positions || [];
        console.log('   🔍 Created positions for file mapping:', createdPositions.map((p: any) => ({
          position_id: p.position_id || p.id,
          position_number: p.position_number,
          quantity: p.quantity
        })));
        
        // Convert Map entries to array for proper iteration
        const fileEntries = Array.from(positionFiles.entries());
        console.log('   📊 Files to upload:', fileEntries.length);
        
        for (const [positionIndex, file] of fileEntries) {
          console.log(`\n   📤 Processing file upload for position ${positionIndex}:`);
          try {
            // Find the corresponding created position by position_number
            const formPosition = formData.positions?.[positionIndex];
            const createdPosition = createdPositions.find((p: any) => p.position_number === formPosition?.position_number);
            
            console.log(`      🔍 Position mapping:`, {
              form_position_index: positionIndex,
              form_position_number: formPosition?.position_number,
              created_position_found: !!createdPosition,
              created_position_id: createdPosition?.position_id || createdPosition?.id,
              file_name: file.name,
              file_size: file.size
            });
            
            if (!createdPosition?.position_id) {
              console.error(`      ❌ No position_id found for position ${positionIndex}`);
              uploadErrors.push(`Position ${positionIndex + 1}: Could not find position ID`);
              continue;
            }
            
            console.log(`      � Upload API Call:`, {
              service: 'unifiedOrderService.uploadPositionFiles',
              order_id: caseWithTempOrder.temp_order_id,
              position_id: createdPosition.position_id,
              file_name: file.name,
              file_size: file.size,
              description: formPosition?.notes || `Position ${formPosition?.position_number} file`
            });
            
            const uploadResponse = await unifiedOrderService.uploadPositionFiles(
              caseWithTempOrder.temp_order_id,
              createdPosition.position_id,
              [file],
              formPosition?.notes || `Position ${formPosition?.position_number} file`
            );
            
            console.log(`      📨 Upload API Response:`, {
              success: uploadResponse.success,
              message: uploadResponse.message,
              has_data: !!uploadResponse.data,
              file_data: uploadResponse.data
            });
            
            if (uploadResponse.success) {
              uploadedFilesCount++;
              console.log(`      ✅ File uploaded successfully for position ${positionIndex}: ${file.name}`);
              console.log(`      🗃️ Expected DB Result:`, {
                order_position_files_table: {
                  file_id: 'auto-generated',
                  position_id: createdPosition.position_id,
                  file_path: 'backend-generated-path',
                  filename: file.name,
                  file_size: file.size,
                  description: formPosition?.notes || `Position ${formPosition?.position_number} file`
                },
                file_associations_table: {
                  association_id: 'auto-generated',
                  entity_type: 'order_position',
                  entity_id: createdPosition.position_id,
                  file_id: 'linked-to-uploaded-file'
                }
              });
            } else {
              console.error(`      ❌ File upload failed for position ${positionIndex}:`, uploadResponse.message);
              uploadErrors.push(`Position ${positionIndex + 1}: ${uploadResponse.message}`);
            }
            
          } catch (error: any) {
            console.error(`      💥 File upload error for position ${positionIndex}:`, error);
            uploadErrors.push(`Position ${positionIndex + 1}: ${error.message}`);
          }
        }
      } else {
        console.log('   ⏭️ Skipping file uploads:', {
          reason: !caseWithTempOrder?.temp_order_id ? 'No temp_order_id in response' : 'No files to upload',
          temp_order_id: caseWithTempOrder?.temp_order_id,
          files_count: positionFiles.size
        });
      }
      
      // PHASE 7: COMPLETION ANALYSIS
      console.log('\n🎉 PHASE 7: WORKFLOW COMPLETION ANALYSIS');
      const workflowResults = {
        case_creation: {
          success: true,
          case_id: caseWithTempOrder.case_id || caseWithTempOrder.id,
          temp_order_id: caseWithTempOrder.temp_order_id,
          positions_created: caseWithTempOrder.positions?.length || 0
        },
        file_uploads: {
          attempted: positionFiles.size,
          successful: uploadedFilesCount,
          failed: uploadErrors.length,
          errors: uploadErrors
        },
        database_state: {
          expected_tables_affected: [
            'cases (1 row inserted)',
            'case_metadata (1 row inserted)',
            'temporary_orders (1 row inserted)',
            `order_positions (${caseWithTempOrder.positions?.length || 0} rows inserted)`,
            `order_position_files (${uploadedFilesCount} rows inserted)`,
            `file_associations (${uploadedFilesCount} rows inserted)`
          ]
        },
        workflow_stage: {
          current_status: caseWithTempOrder.status,
          workflow_state: (caseWithTempOrder as any).workflow_state || 'supplier_quote_generated',
          expected_next_steps: [
            'Send quote request email (transition to "supplier_contacted")',
            'Wait for supplier responses',
            'Quote response tracking and selection',
            'Convert to order or close case'
          ],
          workflow_transitions_available: [
            'supplier_quote_generated → supplier_contacted (send email)',
            'supplier_contacted → supplier_response_received (response)',
            'supplier_response_received → quote_selected (selection)',
            'quote_selected → converted_to_order (conversion)',
            'any_state → case_closed (manual close)'
          ]
        }
      };
      console.log('   📊 Complete Workflow Results:', workflowResults);
      
      // Show completion notification based on actual upload results
      if (uploadedFilesCount > 0) {
        if (uploadErrors.length > 0) {
          showNotification(
            `Supplier case created successfully! ${uploadedFilesCount} file(s) uploaded, ${uploadErrors.length} failed.`, 
            'warning'
          );
          console.log('⚠️ UPLOAD ERRORS:', uploadErrors);
        } else {
          showNotification(`Supplier case created successfully with ${uploadedFilesCount} file(s) uploaded!`, 'success');
        }
        console.log(`📊 CASE CREATION SUMMARY: ${uploadedFilesCount} files uploaded successfully`);
      } else if (positionFiles.size > 0) {
        showNotification('Supplier case created, but file uploads failed. Please upload files manually.', 'warning');
        console.log('❌ CASE CREATION SUMMARY: No files were uploaded due to errors');
      } else {
        showNotification('Supplier case created successfully!', 'success');
        console.log('📊 CASE CREATION SUMMARY: No files to upload');
      }

      // PHASE 8: WORKFLOW NEXT STEPS IDENTIFICATION
      console.log('\n🔄 PHASE 8: WORKFLOW NEXT STEPS IDENTIFICATION');
      console.log('   🎯 Current Workflow State:', (caseWithTempOrder as any).workflow_state || 'supplier_quote_generated');
      console.log('   📋 Next Steps Available:');
      console.log('      1. Send Quote Request Email (GeneralizedCaseDashboard)');
      console.log('      2. Track supplier responses');
      console.log('      3. Quote selection and comparison');
      console.log('      4. Convert to order workflow');
      console.log('   💡 Workflow Integration Status:');
      console.log('      ✅ Case creation with workflow state');
      console.log('      ✅ Workflow transition logging');
      console.log('      🔄 Ready for email sending workflow');
      console.log('      🔄 Ready for response tracking workflow');
      console.log('      3. No quote response deadline tracking');
      console.log('      4. Missing integration with QuoteAggregationManager');
      console.log('   💡 Recommended Workflow Enhancements:');
      console.log('      - Add workflow_stages table integration');
      console.log('      - Implement supplier_quote_responses tracking');
      console.log('      - Add email notification confirmation');
      console.log('      - Create quote compilation workflow bridge');

      // Complete and close
      onCaseCreated(createdCase);
      onClose();
      resetForm();
      
      console.log('\n✅ =============== SUPPLIER CASE CREATION WORKFLOW COMPLETE ===============');
      
    } catch (error: any) {
      console.error('\n💥 =============== SUPPLIER CASE CREATION WORKFLOW FAILED ===============');
      console.error('   Error Phase: During API call or file upload');
      console.error('   Error Details:', {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      showNotification(error.message || 'Failed to create case', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    // Update the ref to prevent ORDER TYPE CHANGE EFFECT when resetting
    previousOrderType.current = 'material';
    
    // Reset the position files map
    setPositionFiles(new Map());
    
    setFormData({
      case_sub_type: initialCaseSubType,
      suppliers: [{ id: '1', email: '', name: '', company: '' }],
      title: '',
      description: '',
      priority: 3,
      order_type: 'material',
      order_date: new Date().toISOString().split('T')[0], // Default to today
      due_date: '',
      order_notes: '',
      positions: [{ ...defaultPosition }],
      auto_reply_enabled: true
    });
    setStep(0);
    setValidationErrors({});
  };

  // Render type-specific fields using proper components
  const renderTypeSpecificFields = (position: PositionData, index: number) => {
    const data = position.type_specific_data;
    
    const handleTypeSpecificChange = (newData: any) => {
      updatePosition(index, 'type_specific_data', newData);
    };
    
    switch (formData.order_type) {
      case 'carbide':
        return (
          <CarbideFields
            data={data as CarbideData}
            onChange={handleTypeSpecificChange}
          />
        );

      case 'material':
        return (
          <MaterialFields
            data={data as MaterialData}
            onChange={handleTypeSpecificChange}
          />
        );

      case 'tool':
        return (
          <ToolFields
            data={data as ToolData}
            onChange={handleTypeSpecificChange}
          />
        );

      case 'coating':
        return (
          <CoatingFields
            data={data as CoatingData}
            onChange={handleTypeSpecificChange}
          />
        );
        
      case 'heat_treatment':
        return (
          <HeatTreatmentFields
            data={data as HeatTreatmentData}
            onChange={handleTypeSpecificChange}
          />
        );

      default:
        return null;
    }
  };

  // Render step content
  const renderStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0: // Case Type & Suppliers
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Case Type</InputLabel>
                <Select
                  value={formData.case_sub_type}
                  onChange={(e) => updateFormData('case_sub_type', e.target.value)}
                  label="Case Type"
                >
                  <MenuItem value="quote_request">
                    <Box display="flex" alignItems="center" gap={1}>
                      <QuoteIcon /> Quote Request
                    </Box>
                  </MenuItem>
                  <MenuItem value="order_issue">
                    <Box display="flex" alignItems="center" gap={1}>
                      <IssueIcon /> Order Issue
                    </Box>
                  </MenuItem>
                  <MenuItem value="follow_up">
                    <Box display="flex" alignItems="center" gap={1}>
                      <FollowUpIcon /> Follow-up
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Suppliers ({formData.suppliers.length}/12)
                </Typography>
                <Button
                  startIcon={<PersonAddIcon />}
                  onClick={addSupplier}
                  disabled={formData.suppliers.length >= 12}
                  variant="outlined"
                  size="small"
                >
                  Add Supplier
                </Button>
              </Box>

              {validationErrors.suppliers && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {validationErrors.suppliers}
                </Alert>
              )}

              {formData.suppliers.map((supplier, index) => (
                <Paper key={supplier.id} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={10}>
                      <SupplierSelector
                        onSupplierSelected={(selectedSupplier) => handleSupplierSelected(index, selectedSupplier)}
                        selectedSupplier={getSelectedSupplierForIndex(index)}
                        defaultEmail={supplier.email || (emailContext && index === 0 ? emailContext.from_address : '')}
                        showAddButton={true}
                        label={`Lieferant ${index + 1}`}
                        required={true}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <IconButton
                        onClick={() => removeSupplier(index)}
                        disabled={formData.suppliers.length === 1}
                        color="error"
                        sx={{ height: '56px' }} // Match SupplierSelector height
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Grid>
        );

      case 1: // Case Details
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title *"
                value={formData.title}
                onChange={(e) => updateFormData('title', e.target.value)}
                error={!!validationErrors.title}
                helperText={validationErrors.title}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={formData.description || ''}
                onChange={(e) => updateFormData('description', e.target.value)}
              />
            </Grid>
            
            {formData.case_sub_type === 'quote_request' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Order Type</InputLabel>
                    <Select
                      value={formData.order_type || 'material'}
                      onChange={(e) => updateFormData('order_type', e.target.value)}
                      label="Order Type"
                    >
                      <MenuItem value="material">Material</MenuItem>
                      <MenuItem value="carbide">Carbide</MenuItem>
                      <MenuItem value="tool">Tool</MenuItem>
                      <MenuItem value="coating">Coating</MenuItem>
                      <MenuItem value="heat_treatment">Heat Treatment</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Order Date"
                    type="date"
                    value={formData.order_date || ''}
                    onChange={(e) => updateFormData('order_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Due Date"
                    type="date"
                    value={formData.due_date || ''}
                    onChange={(e) => updateFormData('due_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Order Notes"
                    value={formData.order_notes || ''}
                    onChange={(e) => updateFormData('order_notes', e.target.value)}
                    placeholder="Order-level notes (separate from position notes)"
                  />
                </Grid>
              </>
            )}

            {formData.case_sub_type === 'order_issue' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Related Order ID *"
                  value={formData.related_order_id || ''}
                  onChange={(e) => updateFormData('related_order_id', e.target.value)}
                  error={!!validationErrors.related_order_id}
                  helperText={validationErrors.related_order_id}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => updateFormData('priority', e.target.value)}
                  label="Priority"
                >
                  <MenuItem value={1}>1 - Low</MenuItem>
                  <MenuItem value={2}>2 - Normal</MenuItem>
                  <MenuItem value={3}>3 - Standard</MenuItem>
                  <MenuItem value={4}>4 - High</MenuItem>
                  <MenuItem value={5}>5 - Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 2: // Positions & Specifications
        if (formData.case_sub_type !== 'quote_request') {
          return (
            <Alert severity="info">
              Position details are only required for quote requests.
            </Alert>
          );
        }

        return (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">
                {formData.order_type?.toUpperCase()} Positions
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addPosition}
                variant="outlined"
              >
                Add Position
              </Button>
            </Box>

            {validationErrors.positions && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {validationErrors.positions}
              </Alert>
            )}

            {formData.positions?.map((position, index) => (
              <Accordion key={index} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    Position {position.position_number}: {position.part_name || position.notes || 'New Position'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {/* Basic Position Fields */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Part Name"
                        value={position.part_name || ''}
                        onChange={(e) => updatePosition(index, 'part_name', e.target.value)}
                        size="small"
                        placeholder="Name of the part or component"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Part Number"
                        value={position.part_number || ''}
                        onChange={(e) => updatePosition(index, 'part_number', e.target.value)}
                        size="small"
                        placeholder="Internal or supplier part number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Menge *"
                        type="number"
                        placeholder="z.B. 10"
                        value={position.quantity === 0 ? '' : position.quantity}
                        onChange={(e) => updatePosition(index, 'quantity', parseInt(e.target.value) || 0)}
                        size="small"
                        required
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Notes"
                        value={position.notes || ''}
                        onChange={(e) => updatePosition(index, 'notes', e.target.value)}
                        size="small"
                        placeholder="Additional notes for this position"
                      />
                    </Grid>

                    {/* Type-specific fields */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        {formData.order_type?.toUpperCase()} Specific Fields
                      </Typography>
                      {renderTypeSpecificFields(position, index)}
                    </Grid>

                    {/* File upload */}
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<UploadIcon />}
                          size="small"
                        >
                          Upload File
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.dwg,.step,.iges,.jpg,.png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileUpload(index, file);
                            }}
                          />
                        </Button>
                        {position.uploadedFile && (
                          <Chip
                            icon={<AttachFileIcon />}
                            label={position.uploadedFile.filename}
                            color="success"
                            size="small"
                          />
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="end">
                        <IconButton
                          onClick={() => removePosition(index)}
                          disabled={formData.positions!.length === 1}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );

      case 3: // Review & Create
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Supplier Case
            </Typography>
            
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Case Information
                </Typography>
                <Typography><strong>Type:</strong> {formData.case_sub_type.replace('_', ' ').toUpperCase()}</Typography>
                <Typography><strong>Title:</strong> {formData.title}</Typography>
                <Typography><strong>Priority:</strong> {formData.priority}/5</Typography>
                {formData.order_type && (
                  <Typography><strong>Order Type:</strong> {formData.order_type.toUpperCase()}</Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Suppliers ({formData.suppliers.length})
                </Typography>
                <List dense>
                  {formData.suppliers.map((supplier, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={supplier.email}
                        secondary={supplier.company ? `${supplier.name} @ ${supplier.company}` : supplier.name}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>

            {formData.positions && formData.positions.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Positions ({formData.positions.length})
                  </Typography>
                  <List dense>
                    {formData.positions.map((position, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${position.position_number}: ${position.part_name || position.notes || 'Position ' + position.position_number}`}
                          secondary={`Qty: ${position.quantity} | Type: ${formData.order_type}`}
                        />
                        {position.uploadedFile && (
                          <ListItemSecondaryAction>
                            <Chip icon={<AttachFileIcon />} label={position.uploadedFile.filename} size="small" color="success" />
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            {formData.auto_reply_enabled && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Automatic confirmation emails will be sent to all suppliers.
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon />
          Create Supplier Case
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={step} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Box sx={{ mt: 2 }}>
          {renderStepContent(step)}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {step > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {step < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? null : <CheckCircleIcon />}
          >
            {loading ? 'Creating Case...' : 'Create Case'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
